import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const services = [
  'Furniture Removal',
  'Appliance Removal',
  'Mattress Removal',
  'Home Cleanout',
  'Barn Cleanout',
  'Garage Cleanout',
  'Attic/Basement Cleanout',
  'Foreclosure Cleanout',
  'Debris Removal',
  'Yard Cleanup',
  'Office Cleanout',
  'Demolition',
  'Deck Removal',
  'Hot Tub Removal',
  'Dump Trailer Rental',
  'Other',
];

export default function QuoteForm({ compact = false }) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: '',
      phone: '',
      email: '',
      service_address: '',
      service_needed: '',
      description: '',
      preferred_contact: 'phone',
      urgency: 'flexible',
      honeypot: '',
    },
  });

  const onSubmit = async (data) => {
    // Check honeypot
    if (data.honeypot) {
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/api/quote-request`, data);
      setIsSuccess(true);
      toast.success("Quote request sent! We'll be in touch soon.");
      reset();
      
      // Reset success state after 5 seconds
      setTimeout(() => setIsSuccess(false), 5000);
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error("Something went wrong. Please call us at (918) 321-0654 or try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="bg-white p-8 rounded-xl text-center" data-testid="form-success">
        <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
        <h3 className="font-['Barlow_Condensed'] text-2xl font-bold text-[#1a1a1a] mb-2">
          Thanks! Your Request Is In.
        </h3>
        <p className="text-gray-600">
          We'll get back to you as quickly as possible. For the fastest response, text photos to (918) 321-0654.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" data-testid="quote-form">
      {/* Honeypot field - hidden from users */}
      <div className="honeypot-field" aria-hidden="true">
        <input
          type="text"
          {...register('honeypot')}
          tabIndex="-1"
          autoComplete="off"
        />
      </div>

      <div className={compact ? "space-y-4" : "grid grid-cols-1 md:grid-cols-2 gap-4"}>
        {/* Name */}
        <div className="space-y-2">
          <Label htmlFor="name" className="text-sm font-semibold">
            Name <span className="text-[#dc2626]">*</span>
          </Label>
          <Input
            id="name"
            {...register('name', { required: 'Name is required' })}
            placeholder="Your name"
            className={`form-input ${errors.name ? 'border-red-500' : ''}`}
            data-testid="form-name"
          />
          {errors.name && (
            <p className="text-red-500 text-xs">{errors.name.message}</p>
          )}
        </div>

        {/* Phone */}
        <div className="space-y-2">
          <Label htmlFor="phone" className="text-sm font-semibold">
            Phone <span className="text-[#dc2626]">*</span>
          </Label>
          <Input
            id="phone"
            type="tel"
            {...register('phone', { required: 'Phone is required' })}
            placeholder="(555) 555-5555"
            className={`form-input ${errors.phone ? 'border-red-500' : ''}`}
            data-testid="form-phone"
          />
          {errors.phone && (
            <p className="text-red-500 text-xs">{errors.phone.message}</p>
          )}
        </div>
      </div>

      <div className={compact ? "space-y-4" : "grid grid-cols-1 md:grid-cols-2 gap-4"}>
        {/* Email */}
        <div className="space-y-2">
          <Label htmlFor="email" className="text-sm font-semibold">
            Email
          </Label>
          <Input
            id="email"
            type="email"
            {...register('email')}
            placeholder="your@email.com"
            className="form-input"
            data-testid="form-email"
          />
        </div>

        {/* Service Address */}
        <div className="space-y-2">
          <Label htmlFor="service_address" className="text-sm font-semibold">
            Service Address <span className="text-[#dc2626]">*</span>
          </Label>
          <Input
            id="service_address"
            {...register('service_address', { required: 'Service address is required' })}
            placeholder="123 Main St, Tahlequah, OK"
            className={`form-input ${errors.service_address ? 'border-red-500' : ''}`}
            data-testid="form-address"
          />
          {errors.service_address && (
            <p className="text-red-500 text-xs">{errors.service_address.message}</p>
          )}
        </div>
      </div>

      {/* Service Needed */}
      <div className="space-y-2">
        <Label htmlFor="service_needed" className="text-sm font-semibold">
          Service Needed <span className="text-[#dc2626]">*</span>
        </Label>
        <Select
          onValueChange={(value) => setValue('service_needed', value)}
          data-testid="form-service-select"
        >
          <SelectTrigger className={`form-input ${errors.service_needed ? 'border-red-500' : ''}`}>
            <SelectValue placeholder="Select a service" />
          </SelectTrigger>
          <SelectContent>
            {services.map((service) => (
              <SelectItem key={service} value={service}>
                {service}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <input
          type="hidden"
          {...register('service_needed', { required: 'Please select a service' })}
        />
        {errors.service_needed && (
          <p className="text-red-500 text-xs">{errors.service_needed.message}</p>
        )}
      </div>

      {/* Description */}
      <div className="space-y-2">
        <Label htmlFor="description" className="text-sm font-semibold">
          Description
        </Label>
        <Textarea
          id="description"
          {...register('description')}
          placeholder="Tell us about your junk removal needs..."
          rows={3}
          className="form-input resize-none"
          data-testid="form-description"
        />
      </div>

      <div className={compact ? "space-y-4" : "grid grid-cols-1 md:grid-cols-2 gap-4"}>
        {/* Preferred Contact */}
        <div className="space-y-2">
          <Label className="text-sm font-semibold">Preferred Contact Method</Label>
          <Select
            defaultValue="phone"
            onValueChange={(value) => setValue('preferred_contact', value)}
          >
            <SelectTrigger className="form-input" data-testid="form-contact-method">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="phone">Phone</SelectItem>
              <SelectItem value="text">Text</SelectItem>
              <SelectItem value="email">Email</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Urgency */}
        <div className="space-y-2">
          <Label className="text-sm font-semibold">How Soon Do You Need Service?</Label>
          <Select
            defaultValue="flexible"
            onValueChange={(value) => setValue('urgency', value)}
          >
            <SelectTrigger className="form-input" data-testid="form-urgency">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="same_day">Same Day</SelectItem>
              <SelectItem value="soon">Within a Few Days</SelectItem>
              <SelectItem value="flexible">Flexible</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Submit Button */}
      <Button
        type="submit"
        disabled={isSubmitting}
        className="w-full bg-[#dc2626] hover:bg-[#b91c1c] text-white font-bold uppercase tracking-wider py-6 btn-press"
        data-testid="form-submit-btn"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            Sending...
          </>
        ) : (
          'Request a Free Estimate'
        )}
      </Button>

      {/* Helper Text */}
      <p className="text-sm text-gray-500 text-center">
        For the fastest quote, text photos to{' '}
        <a href="tel:9183210654" className="text-[#dc2626] font-semibold hover:underline">
          (918) 321-0654
        </a>
      </p>
    </form>
  );
}
